import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { loadStripe } from '@stripe/stripe-js';
import { 
    Elements, 
    CardElement, 
    PaymentRequestButtonElement,
    useStripe, 
    useElements 
} from '@stripe/react-stripe-js';
import { paymentAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { trackPurchase } from '../../lib/metaPixel';
import { 
    CreditCard, 
    Lock, 
    Shield, 
    CheckCircle2, 
    Loader2,
    ArrowLeft,
    Star,
    Zap,
    Clock
} from 'lucide-react';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Card Element styling
const cardElementOptions = {
    style: {
        base: {
            fontSize: '16px',
            color: '#1f2937',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            '::placeholder': {
                color: '#9ca3af',
            },
            padding: '12px',
        },
        invalid: {
            color: '#ef4444',
            iconColor: '#ef4444',
        },
    },
    hidePostalCode: true,
};

// Payment Card Logos Component - Clean Professional Design
const PaymentLogos = () => (
    <div className="flex items-center justify-center gap-2 flex-wrap">
        {/* Visa */}
        <div className="h-9 w-14 bg-white rounded-lg border border-neutral-200 flex items-center justify-center overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <svg viewBox="0 0 780 500" className="h-6 w-10">
                <path d="M293.2 348.7l33.4-195.8h53.3l-33.4 195.8zM540.7 156.4c-10.6-4-27.1-8.2-47.8-8.2-52.8 0-90 26.6-90.3 64.6-.3 28.1 26.5 43.8 46.8 53.2 20.8 9.6 27.8 15.8 27.7 24.4-.1 13.2-16.6 19.2-32 19.2-21.4 0-32.8-3-50.4-10.2l-6.9-3.1-7.5 44c12.5 5.5 35.7 10.2 59.7 10.5 56.2 0 92.6-26.3 93.1-66.8.2-22.3-14-39.2-44.8-53.2-18.7-9.1-30.1-15.1-30-24.3 0-8.1 9.7-16.8 30.6-16.8 17.4-.3 30.1 3.5 39.9 7.5l4.8 2.3 7.2-42.7zm137.8-3.5h-41.3c-12.8 0-22.4 3.5-28 16.3l-79.3 179.5h56.1s9.2-24.2 11.2-29.5h68.5c1.6 6.9 6.5 29.5 6.5 29.5h49.6l-43.3-195.8zm-65.9 126.4c4.4-11.3 21.4-54.8 21.4-54.8-.3.5 4.4-11.4 7.1-18.8l3.6 17s10.3 47 12.5 56.7h-44.6zM232.4 152.9l-52.3 133.5-5.6-27.2c-9.7-31.2-39.9-65-73.7-81.9l47.8 171.2h56.6l84.1-195.6z" fill="#1A1F71"/>
                <path d="M100.6 152.9H15.7l-.7 4c67.1 16.2 111.5 55.4 130 102.5l-18.8-90.2c-3.2-12.3-12.6-15.9-25.6-16.3z" fill="#F9A533"/>
            </svg>
        </div>
        
        {/* Mastercard */}
        <div className="h-9 w-14 bg-white rounded-lg border border-neutral-200 flex items-center justify-center overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <svg viewBox="0 0 780 500" className="h-6 w-10">
                <circle cx="250" cy="250" r="150" fill="#EB001B"/>
                <circle cx="530" cy="250" r="150" fill="#F79E1B"/>
                <path d="M390 100c-46.4 35.8-76.3 91.5-76.3 154s29.9 118.2 76.3 154c46.4-35.8 76.3-91.5 76.3-154s-29.9-118.2-76.3-154z" fill="#FF5F00"/>
            </svg>
        </div>
        
        {/* American Express */}
        <div className="h-9 w-14 bg-[#016FD0] rounded-lg border border-neutral-200 flex items-center justify-center overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <svg viewBox="0 0 780 500" className="h-5 w-9">
                <path d="M0 0h780v500H0z" fill="#016FD0"/>
                <path d="M311.6 249.6H268v-23.3h47.9l6.9-23.9h-78.2v94.4h80.3l-13.3-23.8h-43.6v-23.4h43.6zM401.3 202.4l-29.4 47.2-29.7-47.2h-30l45.2 67.9-45.8 29.2V296.8h28.3l31.8-50.6 31.5 50.6h28.5l.2-2.6-45.6-67.6 44.8-24.2h-29.8zM463.5 272.8v-70.4h-23.4v94.4h71.7l13.3-24h-61.6zM539.5 202.4h-23.4v94.4h80.3l-13.3-24h-43.6zM638.3 202.4l-45.3 94.4h26.2l8.5-17.9h50.6l8.7 17.9h26.5l-45.9-94.4h-29.3zm2.5 53.3l15.4-32.2 15.6 32.2h-31z" fill="#fff"/>
            </svg>
        </div>
        
        {/* Discover */}
        <div className="h-9 w-14 bg-white rounded-lg border border-neutral-200 flex items-center justify-center overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <svg viewBox="0 0 780 500" className="h-6 w-10">
                <path d="M55 0h670c30.4 0 55 24.6 55 55v390c0 30.4-24.6 55-55 55H55c-30.4 0-55-24.6-55-55V55C0 24.6 24.6 0 55 0z" fill="#fff"/>
                <path d="M416 250c0 75-60.8 135.8-135.8 135.8-75 0-135.8-60.8-135.8-135.8 0-75 60.8-135.8 135.8-135.8 75 0 135.8 60.8 135.8 135.8" fill="#F47216"/>
                <path d="M55 250h89.4c16.6 0 27.8 3 36.2 9.8 9.4 7.6 15 19.2 15 32.4 0 13.6-5.8 25.2-15.6 32.8-8.6 6.6-19.6 9.6-36.6 9.6H55V250zm24.4 64.4h28.8c10 0 17.2-1.8 22.2-5.6 5.4-4 8.4-10.4 8.4-18.2s-3-14-8.2-18c-5-3.8-12.2-5.6-22.4-5.6H79.4v47.4z" fill="#231F20"/>
                <text x="440" y="290" fontFamily="Arial, sans-serif" fontSize="100" fontWeight="bold" fill="#231F20">DISCOVER</text>
            </svg>
        </div>
        
        {/* Apple Pay */}
        <div className="h-9 w-14 bg-black rounded-lg border border-neutral-700 flex items-center justify-center overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <svg viewBox="0 0 165 105" className="h-5 w-9">
                <path d="M150.7 0H14.3C6.4 0 0 6.4 0 14.3v76.4C0 98.6 6.4 105 14.3 105h136.4c7.9 0 14.3-6.4 14.3-14.3V14.3C165 6.4 158.6 0 150.7 0z" fill="#000"/>
                <path d="M43.5 34.8c2.2-2.8 3.7-6.6 3.3-10.5-3.2.1-7.1 2.1-9.4 4.9-2 2.3-3.8 6.1-3.3 9.7 3.5.3 7.2-1.8 9.4-4.1zm3.2 5.3c-5.2-.3-9.6 3-12.1 3s-6.3-2.8-10.5-2.8c-5.4.1-10.4 3.1-13.2 8-5.6 9.7-1.4 24.2 4 32.1 2.7 3.9 5.9 8.2 10.1 8.1 4-.2 5.6-2.6 10.5-2.6s6.3 2.6 10.6 2.5c4.4-.1 7.1-3.9 9.8-7.8 3.1-4.5 4.3-8.8 4.4-9-.1-.1-8.5-3.3-8.6-13-.1-8.1 6.6-12 6.9-12.2-3.8-5.6-9.6-6.2-11.7-6.3h-.2zm41.8-11.1v59.1h9.1V67.7h12.6c11.5 0 19.6-7.9 19.6-19.4s-8-19.3-19.3-19.3H88.5zm9.1 8h10.5c7.9 0 12.4 4.2 12.4 11.6s-4.5 11.6-12.4 11.6H97.6V37zm53.5 51.6c5.7 0 11-2.9 13.4-7.5h.2v7h8.4V56.9c0-8.4-6.7-13.8-17.1-13.8-9.5 0-16.6 5.5-16.9 13h8.2c.7-3.6 4.1-5.9 8.4-5.9 5.4 0 8.5 2.5 8.5 7.2v3.2l-11.1.7c-10.3.6-15.9 4.8-15.9 12.1 0 7.5 5.9 12.5 13.9 12.5v-.3zm2.4-6.8c-4.7 0-7.7-2.3-7.7-5.7 0-3.6 2.9-5.6 8.4-6l9.9-.6v3.3c0 5.2-4.4 9-10.6 9zm27.3 22.2c8.9 0 13.1-3.4 16.7-13.7l16-44.3h-9.3l-10.7 34.5h-.2l-10.7-34.5h-9.6l15.4 42.6-.8 2.6c-1.4 4.3-3.6 6-7.5 6-.7 0-2-.1-2.5-.2v7c.5.1 2.4.2 3.2.2v-.2z" fill="#fff"/>
            </svg>
        </div>
        
        {/* Google Pay */}
        <div className="h-9 w-14 bg-white rounded-lg border border-neutral-200 flex items-center justify-center overflow-hidden shadow-sm hover:shadow-md transition-shadow">
            <svg viewBox="0 0 435.97 173.13" className="h-5 w-9">
                <path d="M206.2 84.58v50.75h-16.1V10h42.7a38.61 38.61 0 0127.65 10.85A34.88 34.88 0 01272 47.3a34.68 34.68 0 01-11.55 26.6q-11.2 10.53-27.65 10.52H206.2zm0-59.15v43.72h27a21.28 21.28 0 0015.93-6.48 21.36 21.36 0 000-30.63 21 21 0 00-15.93-6.61z" fill="#5f6368"/>
                <path d="M309.1 46.78q17.85 0 28.18 9.54t10.32 26.16v52.85h-15.4v-11.9h-.7q-10 14.63-26.6 14.62-14.18 0-23.63-8.4a26.87 26.87 0 01-9.45-21q0-13.27 10.06-21.17t26.34-7.88q13.87 0 22.93 5.07v-3.57a18.49 18.49 0 00-6.83-14.52 23.42 23.42 0 00-15.93-6 25.53 25.53 0 00-21.52 10.85l-14.18-8.93q12.42-16.1 35.35-16.1zm-20.13 62.3a12.67 12.67 0 005.16 10.32 18.67 18.67 0 0012.25 4.29 24.69 24.69 0 0017.33-7.18 22.44 22.44 0 007.53-17 37 37 0 00-20-5.25q-12.07 0-18.55 4.81t-6.48 9.97z" fill="#5f6368"/>
                <path d="M436 49.5L384.87 173.13h-16.62l19-41.18-33.6-82.45h17.5l23.37 60.55h.35l22.75-60.55z" fill="#5f6368"/>
                <path d="M141.14 73.64A85.79 85.79 0 00139.9 59H72v27.73h38.89a33.33 33.33 0 01-14.38 21.88v17.93h23.21c13.59-12.53 21.42-31.06 21.42-52.9z" fill="#4285f4"/>
                <path d="M72 144c19.43 0 35.79-6.38 47.72-17.38l-23.21-17.93c-6.47 4.38-14.78 6.88-24.51 6.88-18.78 0-34.72-12.66-40.42-29.72H7.67v18.55A72 72 0 0072 144z" fill="#34a853"/>
                <path d="M31.58 85.85a43.14 43.14 0 010-27.7V39.6H7.67a72 72 0 000 64.8z" fill="#fbbc04"/>
                <path d="M72 28.42a39.09 39.09 0 0127.62 10.8l20.55-20.55A69.18 69.18 0 0072 0 72 72 0 007.67 39.6l23.91 18.55c5.7-17.06 21.64-29.73 40.42-29.73z" fill="#ea4335"/>
            </svg>
        </div>
    </div>
);

// Apple Pay / Google Pay Button Component
const WalletPaymentButton = ({ orderDetails, onSuccess }) => {
    const stripe = useStripe();
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [canMakePayment, setCanMakePayment] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        if (!stripe || !orderDetails) return;

        const pr = stripe.paymentRequest({
            country: 'GB',
            currency: 'gbp',
            total: {
                label: orderDetails.name,
                amount: orderDetails.amount,
            },
            requestPayerName: true,
            requestPayerEmail: true,
        });

        pr.canMakePayment().then(result => {
            if (result) {
                setPaymentRequest(pr);
                setCanMakePayment(true);
            }
        });

        pr.on('paymentmethod', async (ev) => {
            try {
                // Create payment intent
                const intentResponse = await paymentAPI.createPaymentIntent({
                    amount: orderDetails.amount,
                    currency: 'gbp',
                    payment_type: orderDetails.type,
                    package_id: orderDetails.packageId,
                    tier: orderDetails.tier,
                    metadata: orderDetails.metadata || {}
                });

                const { client_secret } = intentResponse.data;

                // Confirm payment
                const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
                    client_secret,
                    { payment_method: ev.paymentMethod.id },
                    { handleActions: false }
                );

                if (confirmError) {
                    ev.complete('fail');
                    return;
                }

                ev.complete('success');

                if (paymentIntent.status === 'requires_action') {
                    const { error } = await stripe.confirmCardPayment(client_secret);
                    if (error) {
                        toast.error('Payment failed');
                        return;
                    }
                }

                // Track and confirm
                trackPurchase(orderDetails.name, orderDetails.amount / 100, orderDetails.type);
                await paymentAPI.confirmPayment(paymentIntent.id, {
                    job_preferences: orderDetails.jobPreferences || {}
                });
                onSuccess(paymentIntent.id);

            } catch (err) {
                ev.complete('fail');
                toast.error('Payment failed');
            }
        });
    }, [stripe, orderDetails, onSuccess]);

    if (!canMakePayment || !paymentRequest) {
        return null;
    }

    return (
        <div className="space-y-4">
            <div className="relative">
                <PaymentRequestButtonElement
                    options={{
                        paymentRequest,
                        style: {
                            paymentRequestButton: {
                                type: 'default',
                                theme: 'dark',
                                height: '48px',
                            },
                        },
                    }}
                />
            </div>
            <div className="relative">
                <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-neutral-500">Or pay with card</span>
                </div>
            </div>
        </div>
    );
};

// Checkout Form Component
const CheckoutForm = ({ orderDetails, onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [cardComplete, setCardComplete] = useState(false);
    const { user } = useAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!stripe || !elements) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Create payment intent on backend
            const intentResponse = await paymentAPI.createPaymentIntent({
                amount: orderDetails.amount,
                currency: 'gbp',
                payment_type: orderDetails.type,
                package_id: orderDetails.packageId,
                tier: orderDetails.tier,
                metadata: orderDetails.metadata || {}
            });

            const { client_secret, payment_intent_id } = intentResponse.data;

            // Confirm payment with Stripe
            const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
                client_secret,
                {
                    payment_method: {
                        card: elements.getElement(CardElement),
                        billing_details: {
                            name: user?.name || user?.email,
                            email: user?.email,
                        },
                    },
                }
            );

            if (stripeError) {
                setError(stripeError.message);
                setLoading(false);
                return;
            }

            if (paymentIntent.status === 'succeeded') {
                // Track purchase
                trackPurchase(orderDetails.name, orderDetails.amount / 100, orderDetails.type);
                
                // Notify backend of successful payment with job preferences
                await paymentAPI.confirmPayment(payment_intent_id, {
                    job_preferences: orderDetails.jobPreferences || {}
                });
                
                onSuccess(payment_intent_id);
            }
        } catch (err) {
            console.error('Payment error:', err);
            setError(err.response?.data?.detail || 'Payment failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Apple Pay / Google Pay */}
            <WalletPaymentButton 
                orderDetails={orderDetails} 
                onSuccess={onSuccess} 
            />

            {/* Card Input */}
            <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700">
                    Card Details
                </label>
                <div className="border border-neutral-300 rounded-lg p-4 bg-white focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all">
                    <CardElement 
                        options={cardElementOptions}
                        onChange={(e) => {
                            setCardComplete(e.complete);
                            if (e.error) {
                                setError(e.error.message);
                            } else {
                                setError(null);
                            }
                        }}
                    />
                </div>
                <p className="text-xs text-neutral-500 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Your payment is secured with 256-bit SSL encryption
                </p>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                </div>
            )}

            {/* Submit Button */}
            <Button
                type="submit"
                disabled={!stripe || loading || !cardComplete}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white py-6 text-lg font-semibold"
            >
                {loading ? (
                    <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing Payment...
                    </>
                ) : (
                    <>
                        <Lock className="w-5 h-5 mr-2" />
                        Pay £{(orderDetails.amount / 100).toFixed(2)}
                    </>
                )}
            </Button>

            {/* Trust Badges */}
            <div className="flex items-center justify-center gap-6 pt-2">
                <div className="flex items-center gap-1 text-neutral-500 text-xs">
                    <Shield className="w-4 h-4" />
                    <span>Secure Payment</span>
                </div>
                <div className="flex items-center gap-1 text-neutral-500 text-xs">
                    <CreditCard className="w-4 h-4" />
                    <span>Powered by Stripe</span>
                </div>
            </div>
        </form>
    );
};

// Main Checkout Page
const CheckoutPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const [orderDetails, setOrderDetails] = useState(null);

    useEffect(() => {
        // Get order details from location state
        const state = location.state;
        
        if (!state || !state.type) {
            // Redirect to pricing if no order details
            navigate('/pricing');
            return;
        }

        setOrderDetails(state);
    }, [location, navigate]);

    useEffect(() => {
        if (!isAuthenticated) {
            toast.error('Please log in to continue');
            navigate('/login', { state: { from: location } });
        }
    }, [isAuthenticated, navigate, location]);

    const handlePaymentSuccess = (paymentIntentId) => {
        navigate('/payment/thank-you', { 
            state: { 
                paymentIntentId,
                orderDetails 
            },
            replace: true
        });
    };

    if (!orderDetails) {
        return (
            <Layout>
                <div className="min-h-screen flex items-center justify-center">
                    <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
                </div>
            </Layout>
        );
    }

    const getPackageIcon = () => {
        if (orderDetails.type === 'subscription') {
            return <Star className="w-6 h-6 text-amber-500" />;
        }
        return <Zap className="w-6 h-6 text-primary-600" />;
    };

    const getFeatures = () => {
        if (orderDetails.type === 'subscription') {
            if (orderDetails.tier === 'standard') {
                return [
                    'Submit your CV to recruiters',
                    'Unlock direct recruiter contact details',
                    'Save & shortlist recruiters',
                    'New recruiter alerts',
                    'Cancel anytime'
                ];
            }
            return [
                'Everything in Standard',
                'Priority CV visibility',
                'Featured candidate badge',
                'Early access to new recruiters',
                'Priority support',
                'Cancel anytime'
            ];
        }
        if (orderDetails.packageId === 'starter') {
            return [
                'Up to 100 job applications per month',
                'Applications tailored to each role',
                'Basic CV improvement suggestions',
                'Email support'
            ];
        }
        if (orderDetails.packageId === 'pro_plus') {
            return [
                'Everything in Pro',
                'Premium CV optimisation & full review',
                'Tailored cover letters for every role',
                'Follow-up emails to hiring managers',
                'Dedicated account manager',
                '1-on-1 career coaching session'
            ];
        }
        return [
            'Everything in Starter',
            'Up to 250 job applications per month',
            'Professionally optimised CV',
            'Custom cover letter for each role',
            'Supporting statement writing',
            'Priority support'
        ];
    };

    return (
        <Layout>
            <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Back Button */}
                    <Button
                        variant="ghost"
                        onClick={() => navigate(-1)}
                        className="mb-6 text-neutral-600 hover:text-neutral-900"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Order Summary */}
                        <Card className="h-fit">
                            <CardHeader className="pb-4">
                                <CardTitle className="text-xl">Order Summary</CardTitle>
                                <CardDescription>Review your purchase details</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Package Info */}
                                <div className="flex items-start gap-4 p-4 bg-neutral-50 rounded-xl">
                                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                        {getPackageIcon()}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-neutral-900">
                                            {orderDetails.name}
                                        </h3>
                                        <p className="text-sm text-neutral-500">
                                            {orderDetails.type === 'subscription' 
                                                ? 'Monthly subscription' 
                                                : 'Monthly package'}
                                        </p>
                                        {orderDetails.tier && (
                                            <Badge className="mt-2 bg-primary-100 text-primary-700">
                                                {orderDetails.tier} Plan
                                            </Badge>
                                        )}
                                    </div>
                                </div>

                                {/* Features */}
                                <div className="space-y-3">
                                    <h4 className="text-sm font-medium text-neutral-700">What's included:</h4>
                                    <ul className="space-y-2">
                                        {getFeatures().map((feature, idx) => (
                                            <li key={idx} className="flex items-center gap-2 text-sm text-neutral-600">
                                                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <Separator />

                                {/* Price Breakdown */}
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-neutral-600">Subtotal</span>
                                        <span className="text-neutral-900">
                                            £{(orderDetails.amount / 100).toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-neutral-600">VAT (included)</span>
                                        <span className="text-neutral-500">£0.00</span>
                                    </div>
                                    <Separator className="my-2" />
                                    <div className="flex justify-between text-lg font-semibold">
                                        <span>Total</span>
                                        <span className="text-primary-600">
                                            £{(orderDetails.amount / 100).toFixed(2)}
                                        </span>
                                    </div>
                                    {orderDetails.type === 'subscription' ? (
                                        <p className="text-xs text-neutral-500 flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            Billed monthly. Cancel anytime.
                                        </p>
                                    ) : orderDetails.type === 'multi_apply' && (
                                        <p className="text-xs text-neutral-500 flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            Billed monthly. Cancel anytime.
                                        </p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Payment Form */}
                        <Card>
                            <CardHeader className="pb-4">
                                <CardTitle className="text-xl flex items-center gap-2">
                                    <CreditCard className="w-5 h-5 text-primary-600" />
                                    Payment Details
                                </CardTitle>
                                <CardDescription>
                                    Choose your preferred payment method
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {/* User Info */}
                                <div className="mb-6 p-4 bg-neutral-50 rounded-lg">
                                    <p className="text-sm text-neutral-600">Paying as:</p>
                                    <p className="font-medium text-neutral-900">{user?.email}</p>
                                </div>

                                {/* Stripe Elements Form */}
                                <Elements stripe={stripePromise}>
                                    <CheckoutForm 
                                        orderDetails={orderDetails}
                                        onSuccess={handlePaymentSuccess}
                                    />
                                </Elements>

                                {/* Accepted Payment Methods */}
                                <div className="mt-6 pt-4 border-t border-neutral-200">
                                    <p className="text-xs text-neutral-500 text-center mb-4">
                                        We accept all major payment methods
                                    </p>
                                    <PaymentLogos />
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Security Notice */}
                    <div className="mt-8 text-center">
                        <p className="text-sm text-neutral-500 flex items-center justify-center gap-2">
                            <Shield className="w-4 h-4" />
                            Your payment information is encrypted and secure. We never store your card details.
                        </p>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default CheckoutPage;
